import { useRef, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, Star, ArrowRight, ChevronLeft, ChevronRight, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getProgramCategories,
  getPrograms,
  createProgram,
  updateProgram,
  lmsGetMeLearner,
  lmsGetEnrollments,
  type ProgramCategory,
  type Program,
  type CreateProgramPayload,
  type UpdateProgramPayload,
  type LmsEnrollmentSummary,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ProgramCardSkeletonGrid } from '@/components/skeletons/ProgramCardSkeleton';
import { SlideOver } from '@/components/SlideOver';
import { ProgramForm } from '@/components/admin/ProgramForm';

const FIXED_CATEGORY_TABS = [
  { key: 'tech', label: 'Tech', aliases: ['tech', 'technology'] },
  { key: 'business', label: 'Business', aliases: ['business', 'biz'] },
  { key: 'agriculture', label: 'Agriculture', aliases: ['agriculture', 'agri', 'farming'] },
] as const;

const CARD_COLORS = [
  { bar: 'bg-africa-red', badge: 'bg-africa-red/10 text-africa-red border-africa-red/20', price: 'text-africa-red' },
  { bar: 'bg-africa-orange', badge: 'bg-africa-orange/10 text-africa-orange border-africa-orange/20', price: 'text-africa-orange' },
  { bar: 'bg-africa-yellow', badge: 'bg-africa-yellow/10 text-africa-yellow border-africa-yellow/20', price: 'text-africa-yellow' },
  { bar: 'bg-africa-green', badge: 'bg-africa-green/10 text-africa-green border-africa-green/20', price: 'text-africa-green' },
  { bar: 'bg-africa-blue', badge: 'bg-africa-blue/10 text-africa-blue border-africa-blue/20', price: 'text-africa-blue' },
  { bar: 'bg-africa-purple', badge: 'bg-africa-purple/10 text-africa-purple border-africa-purple/20', price: 'text-africa-purple' },
] as const;

function ProgramCard({
  program,
  theme,
  isAdmin,
  onEdit,
  continueTo,
}: {
  program: Program;
  theme: (typeof CARD_COLORS)[number];
  isAdmin?: boolean;
  onEdit?: (program: Program) => void;
  /** When user is enrolled in a course in this program, link to that course for "Continue" */
  continueTo?: { programSlug: string; courseSlug: string };
}) {
  const count = program.course_count ? parseInt(program.course_count, 10) : 0;
  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group h-full flex flex-col relative">
      {isAdmin && onEdit && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(program); }}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-background/80 border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
          aria-label="Edit program"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex gap-2 mb-4">
          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${theme.badge}`}>
            {count} {count === 1 ? 'course' : 'courses'}
          </span>
        </div>
        <h3 className="font-display font-bold text-lg text-foreground mb-2">{program.title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-5 line-clamp-3">
          {count === 1 ? 'One course' : `${count} courses—from foundations to advanced and project-based tracks.`}
        </p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-5">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Various
          </span>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
          <span className={`text-lg font-display font-bold ${theme.price}`}>
            {continueTo ? 'You\'re enrolled' : 'View courses'}
          </span>
          {continueTo ? (
            <Link
              to={`/courses/${continueTo.programSlug}/courses/${continueTo.courseSlug}`}
              className="text-sm font-semibold text-primary hover:text-primary/90 inline-flex items-center gap-1 transition-colors"
            >
              Continue <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <Link
              to={`/courses/${program.slug}`}
              className="text-sm font-semibold text-primary hover:text-primary/90 inline-flex items-center gap-1 transition-colors"
            >
              Explore <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export function Programs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get('category') ?? '';
  const [categories, setCategories] = useState<ProgramCategory[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const featuredScrollRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [programPanelOpen, setProgramPanelOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [enrollments, setEnrollments] = useState<LmsEnrollmentSummary[]>([]);

  const refetchPrograms = () => {
    getPrograms(categorySlug || undefined).then(setPrograms).catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getProgramCategories(),
      getPrograms(categorySlug || undefined),
    ])
      .then(([cats, progs]) => {
        if (cancelled) return;
        setCategories(cats);
        setPrograms(progs);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [categorySlug]);

  // All programs for featured strip (no filter)
  const [allPrograms, setAllPrograms] = useState<Program[]>([]);
  useEffect(() => {
    let cancelled = false;
    getPrograms().then((p) => { if (!cancelled) setAllPrograms(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Enrollments for signed-in users: reorder programs and show Continue
  useEffect(() => {
    if (!user || user.role === 'admin') {
      setEnrollments([]);
      return;
    }
    let cancelled = false;
    lmsGetMeLearner()
      .then(({ learnerId }) => (learnerId ? lmsGetEnrollments(learnerId) : { enrollments: [] }))
      .then(({ enrollments: list }) => { if (!cancelled) setEnrollments(list); })
      .catch(() => { if (!cancelled) setEnrollments([]); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  // Enrolled programs first (order = first occurrence in enrollments), then rest
  const enrolledProgramOrder = new Map<string, number>();
  const enrolledProgramToCourse: Record<string, { programSlug: string; courseSlug: string }> = {};
  enrollments.forEach((e, i) => {
    const slug = e.course.programSlug;
    if (!enrolledProgramOrder.has(slug)) {
      enrolledProgramOrder.set(slug, i);
      enrolledProgramToCourse[slug] = { programSlug: slug, courseSlug: e.course.slug };
    }
  });
  const sortProgramsWithEnrolledFirst = (list: Program[]) =>
    [...list].sort((a, b) => {
      const aEn = enrolledProgramOrder.has(a.slug);
      const bEn = enrolledProgramOrder.has(b.slug);
      if (aEn && !bEn) return -1;
      if (!aEn && bEn) return 1;
      if (aEn && bEn) return enrolledProgramOrder.get(a.slug)! - enrolledProgramOrder.get(b.slug)!;
      return 0;
    });

  const resolvedCategoryTabs = FIXED_CATEGORY_TABS.map((tab) => {
    const match = categories.find((c) =>
      tab.aliases.some((a) => c.slug.toLowerCase() === a || c.name.toLowerCase().includes(a))
    );
    return {
      key: tab.key,
      label: tab.label,
      slug: match?.slug ?? tab.key,
    };
  });

  const activeCategory = resolvedCategoryTabs.find((t) => t.slug === categorySlug);
  const featuredSource = allPrograms.length ? allPrograms : programs;
  const featuredProgramsRaw = categorySlug
    ? featuredSource.filter((p) => (p.category_slug ?? '') === categorySlug)
    : featuredSource;
  const featuredPrograms = sortProgramsWithEnrolledFirst(featuredProgramsRaw);
  const sortedPrograms = sortProgramsWithEnrolledFirst(programs);

  const scroll = (dir: 'left' | 'right') => {
    featuredScrollRef.current?.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  return (
    <div className="relative overflow-hidden">
      <section className="py-20 md:py-28 px-6 relative">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-africa-yellow/8 blur-[100px]" />
          <div className="absolute bottom-0 right-10 w-64 h-64 rounded-full bg-africa-blue/8 blur-[80px]" />
        </div>
        <div className="max-w-7xl mx-auto relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-africa-blue/10 text-africa-blue text-sm font-medium mb-6 border border-africa-blue/20">
              <Star className="w-4 h-4" />
              Programs & Courses
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
              Build Real Skills.
              <br />
              <span className="text-africa-green">Create Real Opportunities.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-4">
              Hands-on programs in AI, robotics, business, and digital innovation—designed to help you build, launch, and grow in the real world.
            </p>
            <p className="text-muted-foreground max-w-2xl mb-6">
              From scratch or leveling up: every program moves you <span className="text-foreground font-medium">learning → doing → earning</span>.
            </p>
            <ul className="space-y-2 text-muted-foreground max-w-2xl mb-6">
              <li className="flex items-start gap-2">
                <span className="text-africa-green mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-africa-green" aria-hidden />
                Real-world projects, not just theory
              </li>
              <li className="flex items-start gap-2">
                <span className="text-africa-green mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-africa-green" aria-hidden />
                Learn from practitioners in the industry
              </li>
              <li className="flex items-start gap-2">
                <span className="text-africa-green mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-africa-green" aria-hidden />
                Skills that turn into income, jobs, or your own venture
              </li>
              <li className="flex items-start gap-2">
                <span className="text-africa-green mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-africa-green" aria-hidden />
                Join a community of builders and innovators
              </li>
            </ul>
            <p className="text-lg font-display font-semibold text-foreground">
              This isn’t just education. <span className="text-africa-green">It’s a launchpad.</span>
            </p>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="w-full max-w-md aspect-[4/3] rounded-2xl overflow-hidden border border-border bg-muted/50 flex items-center justify-center">
              <span className="text-muted-foreground text-sm">Image</span>
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="px-6 pb-2">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-display font-bold text-foreground mb-3">Browse by category</h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !categorySlug ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
            >
              All
            </button>
            {resolvedCategoryTabs.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setSearchParams({ category: c.slug })}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  categorySlug === c.slug ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured programs strip — invisible scrollbar only */}
      {featuredPrograms.length > 0 && (
        <>
          <section className="px-6 pb-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground">Featured programs</h2>
                  <p className="text-sm text-muted-foreground mt-1">Scroll to explore →</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => scroll('left')} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button type="button" onClick={() => scroll('right')} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-8 px-6">
            <div
              ref={featuredScrollRef}
              className="max-w-7xl mx-auto flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {featuredPrograms.map((program, idx) => {
                const theme = CARD_COLORS[idx % CARD_COLORS.length];
                return (
                  <div key={program.id} className="flex-shrink-0 w-[320px] snap-start">
                    <ProgramCard
                      program={program}
                      theme={theme}
                      continueTo={enrolledProgramToCourse[program.slug]}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Pills: quick links to programs in current filter */}
      {programs.length > 0 && (
        <section className="px-6 pb-4">
          <div className="max-w-7xl mx-auto">
            <p className="text-sm text-muted-foreground mb-2">Jump to program</p>
            <div className="flex flex-wrap gap-2">
              {programs.map((p) => (
                <Link
                  key={p.id}
                  to={`/courses/${p.slug}`}
                  className="px-3 py-1.5 rounded-full text-sm bg-secondary/80 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {p.title}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Grid of programs (filtered by tab) — scrollable on large screens */}
      <section className="px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-2xl font-display font-bold text-foreground">
              {categorySlug ? activeCategory?.label ?? 'Programs' : 'All programs'}
            </h2>
            {isAdmin && (
              <Button
                size="sm"
                className="bg-accent text-accent-foreground hover:bg-gold-light gap-1"
                onClick={() => { setEditingProgram(null); setProgramPanelOpen(true); }}
              >
                <Plus className="w-4 h-4" /> Add program
              </Button>
            )}
          </div>
          {error && (
            <p className="text-destructive mb-4">{error}</p>
          )}
          {loading && (
            <ProgramCardSkeletonGrid count={8} />
          )}
          {!loading && !error && (
            <div className="overflow-x-auto overflow-y-visible">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 min-w-0">
                {sortedPrograms.map((program, idx) => {
                  const theme = CARD_COLORS[idx % CARD_COLORS.length];
                  return (
                    <ProgramCard
                      key={program.id}
                      program={program}
                      theme={theme}
                      isAdmin={isAdmin}
                      onEdit={isAdmin ? (p) => { setEditingProgram(p); setProgramPanelOpen(true); } : undefined}
                      continueTo={enrolledProgramToCourse[program.slug]}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {!loading && !error && programs.length === 0 && (
            <p className="text-muted-foreground">No programs in this category.</p>
          )}
        </div>
      </section>

      <SlideOver
        open={programPanelOpen}
        onClose={() => { setProgramPanelOpen(false); setEditingProgram(null); }}
        title={editingProgram ? 'Edit program' : 'Add program'}
      >
        <ProgramForm
          categories={categories}
          initial={editingProgram}
          onSave={async (payload) => {
            if (editingProgram) {
              await updateProgram(editingProgram.slug, payload as UpdateProgramPayload);
              toast.success('Program updated.');
            } else {
              await createProgram(payload as CreateProgramPayload);
              toast.success('Program created.');
            }
            setProgramPanelOpen(false);
            setEditingProgram(null);
            refetchPrograms();
          }}
          onCancel={() => { setProgramPanelOpen(false); setEditingProgram(null); }}
        />
      </SlideOver>

      <section className="py-16 px-6 bg-primary">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-primary-foreground mb-4">
            Can't decide? Let us help.
          </h2>
          <p className="text-primary-foreground/60 mb-6">
            Choosing the right path can be overwhelming—we’ll simplify it for you.
          </p>
          <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-gold-light rounded-xl px-8">
            <Link to="/contact">Call Us <ArrowRight className="w-5 h-5 ml-1" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
